import { useCallback, useMemo, useState } from 'react';

import type {
  TemplateListApiArg,
} from '@/services/api/generated/templatesV2Service';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';

export interface UseTemplateSearchOptions {
  /** Filter by template category */
  category?: TemplateListApiArg['category'];
  /** Filter by firm ID */
  firmId?: string;
  /** Filter by format */
  format?: TemplateListApiArg['templateFormat'];
  /** Filter by scope */
  scope?: TemplateListApiArg['scope'];
  /** Only show active templates by default */
  status?: TemplateListApiArg['status'];
  /** Max results per page */
  limit?: number;
  /** Debounce delay in ms */
  debounceMs?: number;
}

export function useTemplateSearch(options: UseTemplateSearchOptions = {}) {
  const {
    category,
    firmId,
    format,
    scope,
    status = 'ACTIVE',
    limit = 25,
    debounceMs = 300,
  } = options;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const queryArgs = useMemo<TemplateListApiArg>(
    () => ({
      search: debouncedSearch || undefined,
      category,
      firmId,
      templateFormat: format,
      scope,
      status,
      limit,
    }),
    [debouncedSearch, category, firmId, format, scope, status, limit],
  );

  const { data, isLoading, isFetching, error } = useTemplateListQuery(queryArgs);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchTimer) clearTimeout(searchTimer);
      const timer = setTimeout(() => {
        setDebouncedSearch(value);
      }, debounceMs);
      setSearchTimer(timer);
    },
    [searchTimer, debounceMs],
  );

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    if (searchTimer) clearTimeout(searchTimer);
  }, [searchTimer]);

  return {
    /** Current search input value */
    searchInput,
    /** Update search input (debounced) */
    onSearchChange: handleSearchChange,
    /** Clear search */
    clearSearch,
    /** Template results */
    templates: data?.results ?? [],
    /** Total count of matching templates */
    totalCount: data?.count ?? 0,
    /** Initial loading state */
    isLoading,
    /** Fetching (including background refetch) */
    isFetching,
    /** Error state */
    error,
  };
}
